import React from "react";
import { money } from "./invoiceStats";

// The accent used for the current-period marker on the sparkline. Green is the
// ReceiptQ brand green; red matches the #dc2626 (Tailwind red-600) already used
// for negative/error states across the app (see e.g. TopBar.jsx,
// MergeClientsModal.jsx, TimelineDrawer.jsx), so this doesn't introduce a new
// red into the codebase. The sparkline marker is now the only thing carrying
// the accent — the icon tile it also tinted was removed.
const ACCENTS = {
  green: { mark: "#007E33" },
  red: { mark: "#dc2626" },
};

// dataviz skill, "Figures — when the form is a number": a stat-tile trend
// sparkline is drawn in the de-emphasis hue, with only the current period
// picked out in the accent. #9ca3af is the muted gray already used
// elsewhere in this app for de-emphasized/inactive strokes (Tailwind
// gray-400).
const MUTED_LINE = "#9ca3af";

const W = 140;
const H = 36;
const PAD_X = 3;
// Extra vertical padding (beyond PAD_X) leaves headroom so the accent
// end-marker's ring isn't clipped by the viewBox at the series' extremes.
const PAD_Y = 6;

function Sparkline({ series, mark }) {
  if (!series || series.length === 0) return null;

  const values = series.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // A flat series has zero range; pinning the divisor to 1 keeps the maths
  // finite and draws the line down the middle rather than emitting NaN.
  const range = max - min || 1;
  const step = series.length > 1 ? (W - PAD_X * 2) / (series.length - 1) : 0;

  const coords = series.map((d, i) => {
    const x = PAD_X + i * step;
    const y = H - PAD_Y - ((d.value - min) / range) * (H - PAD_Y * 2);
    return { x, y };
  });

  const points = coords
    .map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const current = coords[coords.length - 1];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={MUTED_LINE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Current period, called out in the accent per the dataviz skill's
          stat-tile figure spec. The white ring keeps it legible where it
          sits on the muted line. */}
      <circle
        cx={current.x}
        cy={current.y}
        r="4"
        fill={mark}
        stroke="#fff"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function StatCard({ label, value, subtitle, series, accent, onClick, active }) {
  const a = ACCENTS[accent] || ACCENTS.green;

  const body = (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="text-2xl font-semibold text-gray-800">{money(value)}</div>
        {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
      </div>
      <Sparkline series={series} mark={a.mark} />
    </div>
  );

  const base = "bg-white rounded-xl border p-4 flex-1 min-w-[220px]";

  // Without onClick this is exactly the card it has always been, so the
  // non-clickable consumers are untouched. With one it becomes a real
  // <button>, which is also what makes it keyboard-reachable.
  if (!onClick) {
    return <div className={`${base} border-gray-200`}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      className={`${base} text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007E33] ${
        active
          ? "border-transparent ring-2 ring-[#007E33]"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {body}
    </button>
  );
}
